import {
  policyNumberLimits,
  type SecurityPolicyDraft,
} from '@/services/business/security/policy';
import { SecurityDomainError } from '@/services/business/security/problems';
import type { SmsProtection } from '@/services/business/security/smsProtection';
import { smsProtectionRanges } from '@/services/business/security/smsProtection';
import {
  type IpRuleValues,
  type LoginPolicyValues,
  type ProtectionPolicyValues,
  protectionActions,
} from '@/services/business/security/types';
import { normalizeIpAddress } from '@/services/business/security/validation';

export function isSmsProtection(value: unknown): value is SmsProtection {
  if (!record(value)) {
    return false;
  }
  const input = value;
  return (
    Object.keys(input).length === Object.keys(smsProtectionRanges).length &&
    Object.entries(smsProtectionRanges).every(
      ([key, [min, max]]) =>
        Number.isInteger(input[key]) &&
        Number(input[key]) >= min &&
        Number(input[key]) <= max,
    )
  );
}
export function isSecurityPolicyDraft(value: unknown): value is SecurityPolicyDraft {
  if (!record(value)) {
    return false;
  }
  const v = value;
  const keys = [
    'mfaRequirement',
    'riskTriggers',
    'riskAction',
    'deliveryMethods',
    'codeMinutes',
    'resendSeconds',
    'maxCodeAttempts',
  ];
  if (
    Object.keys(v).length !== keys.length ||
    !Object.keys(v).every((key) => keys.includes(key))
  ) {
    return false;
  }
  for (const [key, [min, max]] of Object.entries(policyNumberLimits)) {
    if (!Number.isInteger(v[key]) || Number(v[key]) < min || Number(v[key]) > max) {
      return false;
    }
  }
  if (
    typeof v.mfaRequirement !== 'string' ||
    !['disabled', 'optional', 'admins', 'all'].includes(v.mfaRequirement) ||
    typeof v.riskAction !== 'string' ||
    !['challenge', 'deny', 'notify'].includes(v.riskAction)
  ) {
    return false;
  }
  function choices(key: string, allowed: string[], required = false) {
    const list = v[key];
    return (
      Array.isArray(list) &&
      (!required || list.length > 0) &&
      new Set(list).size === list.length &&
      list.every((item: unknown) => typeof item === 'string' && allowed.includes(item))
    );
  }
  return (
    choices('riskTriggers', ['newDevice', 'unusualLocation']) &&
    choices('deliveryMethods', ['sms', 'email'], true) &&
    Number(v.resendSeconds) < Number(v.codeMinutes) * 60
  );
}
function integer(value: unknown, min: number, max: number) {
  return Number.isInteger(value) && Number(value) >= min && Number(value) <= max;
}
function allowedKeys(value: unknown, keys: string[]) {
  return (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value).every((key) => keys.includes(key))
  );
}
export function validateLoginPolicy(input: unknown): LoginPolicyValues {
  if (!isLoginPolicy(input)) {
    throw new SecurityDomainError('invalidPolicy', 422);
  }
  return structuredClone(input);
}
function isLoginPolicy(input: unknown): input is LoginPolicyValues {
  if (!record(input) || !record(input.humanVerification) || !record(input.security)) {
    return false;
  }
  return (
    exactKeys(input, ['maxFailures', 'lockMinutes', 'humanVerification', 'security']) &&
    exactKeys(input.humanVerification, ['mode', 'failureThreshold']) &&
    exactKeys(input.security, ['policyDraft']) &&
    integer(input.maxFailures, 3, 20) &&
    integer(input.lockMinutes, 1, 1440) &&
    typeof input.humanVerification.mode === 'string' &&
    ['off', 'always', 'afterFailures'].includes(input.humanVerification.mode) &&
    integer(input.humanVerification.failureThreshold, 1, 19) &&
    (input.humanVerification.mode !== 'afterFailures' ||
      Number(input.humanVerification.failureThreshold) < Number(input.maxFailures)) &&
    isSecurityPolicyDraft(input.security.policyDraft)
  );
}
export function validateProtectionPolicy(input: unknown): ProtectionPolicyValues {
  if (!isProtectionPolicy(input)) {
    throw new SecurityDomainError('invalidPolicy', 422);
  }
  return structuredClone(input);
}
function isProtectionPolicy(input: unknown): input is ProtectionPolicyValues {
  if (
    !record(input) ||
    !exactKeys(input, ['autoProtection', 'smsProtection']) ||
    !Array.isArray(input.autoProtection) ||
    input.autoProtection.length !== 3 ||
    !isSmsProtection(input.smsProtection)
  ) {
    return false;
  }
  for (const kind of ['loginSpray', 'verificationFlood', 'apiFlood'] as const) {
    const matches: unknown[] = input.autoProtection.filter(
      (rule: unknown) => record(rule) && rule.kind === kind,
    );
    if (matches.length !== 1 || !record(matches[0])) {
      return false;
    }
    const rule = matches[0];
    const ban = rule.ipBan;
    if (
      !allowedKeys(rule, [
        'kind',
        'enabled',
        'windowMinutes',
        'threshold',
        'durationMinutes',
        'action',
        'ipBan',
      ]) ||
      typeof rule.enabled !== 'boolean' ||
      !integer(rule.windowMinutes, 1, 1440) ||
      !integer(rule.threshold, 2, 100000) ||
      !integer(rule.durationMinutes, 1, 1440) ||
      (rule.action !== 'observe' && rule.action !== protectionActions[kind])
    ) {
      return false;
    }
    if (kind === 'loginSpray') {
      if (
        !record(ban) ||
        !exactKeys(ban, ['strategy', 'secondHours', 'subsequentHours', 'resetHours']) ||
        typeof ban.strategy !== 'string' ||
        !['fixed', 'progressive'].includes(ban.strategy) ||
        !integer(ban.secondHours, 1, 168) ||
        !integer(ban.subsequentHours, 1, 168) ||
        !integer(ban.resetHours, 1, 168) ||
        (ban.strategy === 'progressive' &&
          (Number(ban.secondHours) * 60 <= Number(rule.durationMinutes) ||
            Number(ban.subsequentHours) <= Number(ban.secondHours)))
      ) {
        return false;
      }
    } else if (ban !== undefined) {
      return false;
    }
  }
  return true;
}
export function validateIpRule(input: unknown, now: number): IpRuleValues {
  if (
    !record(input) ||
    !exactKeys(input, ['address', 'note', 'enabled', 'expiresAt']) ||
    typeof input.address !== 'string' ||
    typeof input.note !== 'string' ||
    input.note.trim().length > 200 ||
    typeof input.enabled !== 'boolean' ||
    (input.expiresAt !== null &&
      (typeof input.expiresAt !== 'string' ||
        !Number.isFinite(Date.parse(input.expiresAt)) ||
        (input.enabled && Date.parse(input.expiresAt) <= now)))
  ) {
    throw new SecurityDomainError('invalidPolicy', 422);
  }
  return {
    address: normalizeIpAddress(input.address),
    note: input.note.trim(),
    enabled: input.enabled,
    expiresAt: input.expiresAt,
  };
}
export function record(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
function exactKeys(value: Record<string, unknown>, keys: string[]) {
  return (
    Object.keys(value).length === keys.length &&
    Object.keys(value).every((key) => keys.includes(key))
  );
}
export function validateNote(note: unknown) {
  if (typeof note !== 'string' || !note.trim() || note.trim().length > 200) {
    throw new SecurityDomainError('noteRequired', 422);
  }
  return note.trim();
}
export function validateResolution(value: unknown) {
  if (value !== 'safe' && value !== 'mitigated' && value !== 'falsePositive') {
    throw new SecurityDomainError('invalidResolution', 422);
  }
  return value;
}
